import { PageHeader, SectionLabel, Card, Stat, SampleBanner, StatusPill, Row } from '../components/ui.js';

interface Backup {
  date: string;
  type: 'db' | 'files';
  status: 'ok' | 'failed';
  size_mb: number;
  duration_s: number;
  destination: string;
}

const HISTORY: Backup[] = (() => {
  const arr: Backup[] = [];
  for (let i = 0; i < 28; i++) {
    const d = new Date(Date.now() - i * 86400000);
    const date = d.toISOString().slice(0, 10);
    const failed = i === 14; // one 14-day-old failure
    arr.push({ date, type: 'db', status: failed ? 'failed' : 'ok', size_mb: failed ? 0 : Math.round(184 + Math.sin(i * 0.7) * 8), duration_s: failed ? 0 : Math.round(42 + Math.cos(i * 0.5) * 6), destination: 's3://bloom-backups/db/' });
    arr.push({ date, type: 'files', status: 'ok', size_mb: Math.round(2840 + Math.cos(i * 0.4) * 120), duration_s: Math.round(312 + Math.sin(i * 0.3) * 24), destination: 's3://bloom-backups/files/' });
  }
  return arr;
})();

export function BackupsPage() {
  const last24 = HISTORY.filter((b) => new Date(b.date) >= new Date(Date.now() - 86400000));
  const successes = HISTORY.filter((b) => b.status === 'ok').length;
  const failures = HISTORY.filter((b) => b.status === 'failed').length;
  const lastDb = HISTORY.find((b) => b.type === 'db' && b.status === 'ok')!;
  const lastFiles = HISTORY.find((b) => b.type === 'files' && b.status === 'ok')!;
  const totalSize = HISTORY.filter((b) => b.status === 'ok').reduce((s, b) => s + b.size_mb, 0);

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Backups"
        title={<>Encrypted, off-site, <em className="text-cream/80">verified daily.</em></>}
        subtitle="Bloom backs up your database and uploads every night to encrypted off-site storage. Restoration is one click."
      />
      <SampleBanner />

      <section>
        <SectionLabel>Status</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Last 24h" value={`${last24.filter((b) => b.status === 'ok').length}/${last24.length}`} hint="ok / total" tone="ok" />
          <Stat label="28d successes" value={successes} tone="ok" />
          <Stat label="28d failures" value={failures} tone={failures > 0 ? 'warn' : 'ok'} />
          <Stat label="Total stored" value={`${(totalSize / 1024).toFixed(1)} GB`} />
        </div>
      </section>

      <section>
        <SectionLabel>Latest backups</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Database</div>
              <StatusPill tone="ok">verified</StatusPill>
            </div>
            <div className="font-serif text-3xl text-cream tracking-tightest tabular-nums">{lastDb.size_mb} MB</div>
            <dl className="mt-4 space-y-2 text-sm">
              <Row label="Completed">{new Date(lastDb.date).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</Row>
              <Row label="Duration">{lastDb.duration_s}s</Row>
              <Row label="Destination">{lastDb.destination}</Row>
              <Row label="Encryption">AES-256-GCM at rest, TLS 1.3 in transit</Row>
            </dl>
          </Card>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">WordPress files</div>
              <StatusPill tone="ok">verified</StatusPill>
            </div>
            <div className="font-serif text-3xl text-cream tracking-tightest tabular-nums">{(lastFiles.size_mb / 1024).toFixed(2)} GB</div>
            <dl className="mt-4 space-y-2 text-sm">
              <Row label="Completed">{new Date(lastFiles.date).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</Row>
              <Row label="Duration">{Math.floor(lastFiles.duration_s / 60)}m {lastFiles.duration_s % 60}s</Row>
              <Row label="Destination">{lastFiles.destination}</Row>
              <Row label="Encryption">AES-256-GCM at rest, TLS 1.3 in transit</Row>
            </dl>
          </Card>
        </div>
      </section>

      <section>
        <SectionLabel>Retention</SectionLabel>
        <Card>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
            <Row label="Database backups">14 days rolling</Row>
            <Row label="WordPress files">28 days rolling</Row>
            <Row label="Schedule">Nightly, 2:00 AM Phoenix</Row>
          </dl>
        </Card>
      </section>

      <section>
        <SectionLabel>Backup history</SectionLabel>
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-3 py-3 font-medium">Type</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium text-right">Size</th>
                <th className="px-5 py-3 font-medium text-right">Duration</th>
              </tr>
            </thead>
            <tbody>
              {HISTORY.slice(0, 30).map((b, i) => (
                <tr key={`${b.date}-${b.type}-${i}`} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                  <td className="px-5 py-3 text-cream/75">{new Date(b.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                  <td className="px-3 py-3 text-cream/70 text-[12px]">{b.type === 'db' ? 'database' : 'files'}</td>
                  <td className="px-3 py-3"><StatusPill tone={b.status === 'ok' ? 'ok' : 'bad'}>{b.status === 'ok' ? '✓ ok' : 'failed'}</StatusPill></td>
                  <td className="px-3 py-3 text-right tabular-nums text-cream/85">{b.status === 'ok' ? (b.type === 'files' ? `${(b.size_mb / 1024).toFixed(1)} GB` : `${b.size_mb} MB`) : '—'}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-cream/65">{b.status === 'ok' ? `${b.duration_s}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
